import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import newGradBgImg from "images/recruitment/new-grad-recruitment.jpg";
import midBgImg from "images/recruitment/mid-level-recruitment.png";
import RecruitmentCardMobileView from "./RecruitmentCardMobileView";
import SalaryCalculator from "components/Recruitment/SalaryCalculator";
import RecruitmentHero from "components/Recruitment/RecruitmentHero";
import tw from "twin.macro";

const Container = tw.div`relative`;
// Match global header horizontal padding (see `components/hero/StickyHeader.js`)
const Content = tw.div`max-w-screen-xl mx-auto py-10 px-8 sm:px-12`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;

  @media (min-width: 1024px) {
    grid-template-columns: 1fr 400px;
    gap: 3.5rem;
    align-items: start;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const RightColumn = styled.aside`
  @media (min-width: 1024px) {
    position: sticky;
    top: 90px;
    align-self: start;
  }
`;

const SectionTitle = styled.h2`
  font-size: 0.85rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 10px;
  letter-spacing: 0.02em;

  &::before {
    content: "";
    width: 3px;
    height: 14px;
    background: #0c4d96;
    border-radius: 2px;
  }
`;

const JobCardsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;

  @media (min-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.25rem;
  }
`;

const JobCard = styled(motion.article)`
  background: #ffffff;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  border: 1px solid #e8eaed;

  &:hover {
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
    border-color: #0c4d96;
  }
`;

const JobCardImage = styled.div`
  height: 130px;
  background: #f3f4f6;
  position: relative;
  overflow: hidden;
`;

const JobCardImageContent = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;

  ${JobCard}:hover & {
    transform: scale(1.03);
  }
`;

const JobCardBody = styled.div`
  padding: 1.15rem;
`;

const JobCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
`;

const JobCardTitle = styled.h3`
  font-size: 0.9rem;
  font-weight: 700;
  color: #111827;
  line-height: 1.35;
  margin: 0;
`;

const JobCardType = styled.span`
  font-size: 0.6rem;
  font-weight: 600;
  color: #0c4d96;
  white-space: nowrap;
  line-height: 1.3;
  flex-shrink: 0;
  background: #e8f2ff;
  padding: 4px 8px;
  border-radius: 4px;
`;

const JobCardSubtitle = styled.p`
  font-size: 0.78rem;
  color: #6b7280;
  margin: 0 0 0.85rem 0;
  line-height: 1.45;
`;

const JobCardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  margin-bottom: 0.85rem;
  padding-bottom: 0.85rem;
  border-bottom: 1px solid #f0f1f3;
`;

const JobCardMetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.68rem;
  color: #6b7280;

  svg {
    width: 11px;
    height: 11px;
    stroke: #9ca3af;
    fill: none;
    flex-shrink: 0;
  }
`;

const JobCardButton = styled.a`
  display: block;
  width: 100%;
  padding: 9px 12px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: #0c4d96;
    color: white;
    border: none;
    &:hover {
      background: #0a3f7a;
    }
  `
      : `
    background: transparent;
    color: #0c4d96;
    border: 1.5px solid #0c4d96;
    &:hover {
      background: #0c4d96;
      color: white;
    }
  `}
`;

const images = [newGradBgImg, midBgImg];

const ClockIcon = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const LocationIcon = () => (
  <svg viewBox="0 0 24 24" strokeWidth="2">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export default function RecruitmentCardTwoCol({ isXs = false }) {
  const { t } = useTranslation();
  
  if (isXs) return <RecruitmentCardMobileView />;

  const cards = t("recruitment.cards", { returnObjects: true });

  return (
    <Container>
      <Content>
        <MainGrid>
          {/* Left Column: Hero + Job Cards */}
          <LeftColumn>
            <RecruitmentHero />
            
            <SectionTitle>{t("recruitment_cards.open_positions")}</SectionTitle>
            <JobCardsGrid>
              {cards.map((card, index) => (
                <JobCard
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: index * 0.1 }}
                >
                  <JobCardImage>
                    <JobCardImageContent src={images[index]} alt={card.company} />
                  </JobCardImage>
                  <JobCardBody>
                    <JobCardHeader>
                      <JobCardTitle>{card.company}</JobCardTitle>
                      <JobCardType>{card.type}</JobCardType>
                    </JobCardHeader>
                    <JobCardSubtitle>{card.title}</JobCardSubtitle>
                    <JobCardMeta>
                      <JobCardMetaItem>
                        <ClockIcon />
                        {card.durationText}
                      </JobCardMetaItem>
                      <JobCardMetaItem>
                        <LocationIcon />
                        {card.locationText}
                      </JobCardMetaItem>
                    </JobCardMeta>
                    {index === 0 ? (
                      <JobCardButton
                        href={card.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="primary"
                      >
                        {t("recruitment.linkText")}
                      </JobCardButton>
                    ) : (
                      <JobCardButton
                        href={`mailto:${t("footer_contact_email_soumu")}`}
                        variant="outline"
                      >
                        {t("header_nav_contactus")}
                      </JobCardButton>
                    )}
                  </JobCardBody>
                </JobCard>
              ))}
            </JobCardsGrid>
          </LeftColumn>

          {/* Right Column: Salary Calculator */}
          <RightColumn>
            <SalaryCalculator />
          </RightColumn>
        </MainGrid>
      </Content>
    </Container>
  );
}
